import {StaticCreativeFormItem} from 'authoring/src/pages/CreativesLibraryPage/CreativeLibraryPage.types';
import axios, {AxiosProgressEvent} from 'axios';
import {backgroundProcessService} from 'common/src/modules/backgroundProcess/backgroundProcess.service';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {Routes} from 'common/src/modules/routing/routing.config';
import {EntityToRename} from 'common/src/modules/shared/copilot/copilot.types';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import {NewNodeYShift} from '../../components/CLCardsMap/CLCardsMap.config';
import {ImportResponse} from '../../components/CLFileImportModal/CLFileImportModal';
import {extractExternalLinksFromZip} from '../../utils/fileUtils/extractExternalLinksFromZip';
import {AdTagPreviewParams, CampaignAdTags} from '../adTags/adTags.types';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {ApiOptions, ParamsObj, PostDataObj} from '../api/apiTypes';
import {createPathParamsUrl} from '../api/createPathParamsUrl';
import {BGProcessLinkType} from '../backgroundProcess/backgroundProcess.types';
import {LandingPageSource} from '../campaigns/campaigns.enums';
import {TemplateType} from '../creativeTemplates/creativeTemplates.types';
import {handleApiError} from '../errorHandling/handleApiError';
import {SocialAdType} from '../externalAds/externalAds.types';
import {
  basePath as fileUploadBasePath,
  fileUploadApi,
  PreSignedUrlResponse,
  uploadFileToS3,
} from '../fileUpload/fileUpload.api';
import {ImageUploadType, VideoUploadType} from '../fileUpload/fileUpload.types';
import {JobExecutionParams} from '../jobs/jobs.types';
import waitForJob from '../jobs/utils/waitForJob';
import {CLMimeType} from '../media/media.types';
import {DataWithSharingInfo} from '../sharing/sharing.types';
import {toClientCreative, toRemoteDynamicParameters} from './creatives.dataConverters';
import {
  Creative,
  CreativeConditions,
  DynamicParameter,
  MINIMUM_NATIVE_LOGO_SIZE,
  RemoteCreative,
  StaticCreativeType,
} from './creatives.types';

const BASE_PATH = 'Creative';
const V2_BASE_PATH = 'Creatives';
const CAMPAIGN_CREATIVE_BASE_PATH = 'CampaignCreative';
const DEFAULT_LANGUAGE = 'en';

const STACKED_NODES_SHIFT = 40;
export type ParamsForDTCreative = {
  campaignId: number;
  getStyleInstructions?: boolean;
  templateType?: TemplateType[];
  attachedToNodeId?: number;
  attachedToOutputId?: number;
  isRootNode?: boolean;
  isForFallbackCreative?: boolean;
  isForCarousel?: boolean;
  adType?: SocialAdType;
};

export type GetCreativesParams = {
  productType?: number;
  getStyleInstructions?: boolean;
  isIncludeDynamicParameters?: boolean;
  templateType?: TemplateType[];
  clientStyleId?: number;
  adType?: SocialAdType;
  isCatalogFeed?: boolean;
};

async function getCreatives({
  templateType,
  adType,
  ...params
}: GetCreativesParams): Promise<Creative[]> {
  const endPoint = `${BASE_PATH}/Get`;
  const [generalEnums, campaignEnums] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
  ]);

  const apiParams: ParamsObj = {
    ...params,
  };

  if (templateType) {
    apiParams.templateType = templateType;
  }

  if (adType) {
    apiParams.adType = adType;
  }

  const {
    data: [creatives],
  } = await apiService.executeGetRequest<RemoteCreative[][]>(endPoint, apiParams);

  return _map(creatives, (creative) => toClientCreative(generalEnums, campaignEnums, creative));
}

async function get({
  id,
  includeDynamicParams = false,
}: {
  id: number;
  includeDynamicParams?: boolean;
}): Promise<DataWithSharingInfo<Creative, 'creative'>> {
  const [generalEnums, templateEnums, creative] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.Templates),
    apiV2Service
      .executeGetRequest<DataWithSharingInfo<RemoteCreative, 'creative'>>(`${V2_BASE_PATH}/${id}`, {
        includeDynamicParams,
      })
      .then((res) => res.data),
  ]);

  return {
    sharePermissions: creative.sharePermissions,
    creative: toClientCreative(generalEnums, templateEnums, creative.creative),
  };
}

async function getAll({
  productType,
  getStyleInstructions = false,
  isIncludeDynamicParameters = false,
  templateType,
  isCatalogFeed = false,
}: Omit<GetCreativesParams, 'id'>): Promise<Creative[]> {
  const params: GetCreativesParams = {
    templateType,
    getStyleInstructions,
    isIncludeDynamicParameters,
    isCatalogFeed,
  };

  if (productType) {
    params.productType = productType;
  }

  return getCreatives({...params});
}

async function getForDecisionTree(params: ParamsForDTCreative): Promise<Creative[]> {
  const [generalEnums, campaignEnums, creatives] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    apiV2Service
      .executeGetRequest<RemoteCreative[]>(`${V2_BASE_PATH}/GetForDecisionTree`, params)
      .then((res) => res.data),
  ]);

  return _map(creatives, (creative) => toClientCreative(generalEnums, campaignEnums, creative));
}

function getAllSlim(): Promise<DataWithSharingInfo<Creative[], 'creatives'>> {
  return Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    apiV2Service
      .executeGetRequest<
        DataWithSharingInfo<RemoteCreative[], 'creatives'>
      >(`${V2_BASE_PATH}/GetAll`)
      .then((res) => res.data),
  ]).then(([generalEnums, campaignEnums, remoteCreatives]) => ({
    sharePermissions: remoteCreatives.sharePermissions,
    creatives: remoteCreatives.creatives.map((cr) =>
      toClientCreative(generalEnums, campaignEnums, cr),
    ),
  }));
}

function disconnectFromCampaign(data: {campaignId: number; clientStyleId: number}) {
  return apiService.executeDeleteRequest(`${CAMPAIGN_CREATIVE_BASE_PATH}/Delete`, data);
}

function deleteCreative(creativeId: number) {
  return apiService.executeDeleteRequest(`${BASE_PATH}/Delete`, {id: creativeId});
}

async function getCreativePosters(creativeIds: string[] = []): Promise<Partial<Creative[]>> {
  const {data} = await apiV2Service.executePostRequest<Partial<Creative[]>>(
    `${V2_BASE_PATH}/GetCreativePosters`,
    {},
    {
      creativeIds,
    },
  );

  return data;
}

function duplicate(creativeIds: number[]): Promise<number[]> {
  return Promise.all(
    creativeIds.map((creativeId) => {
      return apiV2Service.executePostRequest<number>(`${V2_BASE_PATH}/${creativeId}/duplicate`, {
        creativeId,
      });
    }),
  ).then((responses) => responses.map((response) => response.data));
}

function duplicateFromCSV(creativeId: number, csvFile: File) {
  return apiService
    .uploadFile<[{message: string; executionId: JobExecutionParams}]>({
      endpoint: `${BASE_PATH}/DuplicateClientStylesFromCSV`,
      params: {
        clientStyleId: creativeId,
      },
      file: csvFile,
    })
    .then(
      ({
        data: [
          {
            executionId: {executionId: jobId},
          },
        ],
      }) => waitForJob({jobId, jobName: `Duplicate creative ${creativeId} from CSV`}),
    );
}

function duplicateToClient({
  creativeId,
  targetClientId,
}: {
  creativeId: number;
  targetClientId: number;
}): Promise<number> {
  const url = createPathParamsUrl(`${V2_BASE_PATH}/:creativeId/duplicate`, {
    creativeId,
  });

  return apiV2Service
    .executePostRequest<number>(url, {
      creativeId,
      targetClientId,
    })
    .then((res) => res.data);
}

function getDuplicateFromCSVFileExample(creativeId: number) {
  return apiService.downloadFileRequest({
    endpoint: `${BASE_PATH}/GetDuplicateClientStyleTemplate`,
    paramsObj: {
      clientStyleId: creativeId,
    },
  });
}

function copyDynamicParams(
  originCreativeId: number,
  targetCreativeId: number,
  paramsToCopy: string[],
) {
  return apiService.executePostRequest(
    `${BASE_PATH}/CopyDynamicParameters`,
    {
      originClientStyleId: originCreativeId,
      destinationClientStyleId: targetCreativeId,
    },
    {
      Data: JSON.stringify(paramsToCopy),
    },
  );
}

function connectToCampaign(
  creativeId: number,
  campaignId: number,
  campaignType: number,
  config: CreativeConditions,
) {
  return apiService.executePostRequest(
    `${CAMPAIGN_CREATIVE_BASE_PATH}/AddCampaignClientStyle`,
    {
      campaignId,
      campaignType,
      clientStyleId: creativeId,
    },
    {Data: JSON.stringify(config)},
  );
}

function addFullSizePromoSlide(creativeId: number) {
  return apiService.executePostRequest(`${BASE_PATH}/AddFullSlidePromo`, {
    clientStyleId: creativeId,
  });
}

function removeFullSizePromoSlide(creativeId: number) {
  return apiService.executePostRequest(`${BASE_PATH}/RemoveFullSlidePromo`, {
    clientStyleId: creativeId,
  });
}

function exportToZip(...creativeIds: number[]) {
  return apiService.downloadFileRequest({
    endpoint: `${BASE_PATH}/Export`,
    paramsObj: {
      creativeIds: creativeIds.join(','),
    },
    method: 'post',
  });
}

function getAdTagsForCreativePreview(
  creativeId: number,
  previewParameters: AdTagPreviewParams = {},
  forcedDPKey: string | null = null,
) {
  const payload: ParamsObj = {creativeId};

  if (forcedDPKey) payload.forcedDPKey = forcedDPKey;

  return apiService
    .executePostRequest<[CampaignAdTags]>(`AdTags/GetAdTagsForCreativePreview`, payload, {
      previewParameters: JSON.stringify(previewParameters),
    })
    .then(({data}) => data[0]);
}

function createFromTemplate({
  name,
  country,
  templateId,
}: {
  name: string;
  country: string;
  templateId: number;
}) {
  return apiService
    .executePostRequest<number[]>(`${BASE_PATH}/Add`, {
      name,
      country,
      baseStyleId: templateId,
    })
    .then((res) => res.data[0]);
}

async function createFromUpload({
  name,
  country,
  file,
  creativeType,
  isRetinaReady,
}: {
  name: string;
  country: string;
  file: File;
  creativeType: StaticCreativeType;
  isRetinaReady?: boolean;
}) {
  const process = backgroundProcessService.startProcess(`Upload Static Creative`);
  process.setSubtitle(name);

  if (+creativeType === StaticCreativeType.Video && !file.type.includes('zip')) {
    const {
      data: [urlsObj],
    } = await apiService.executeGetRequest<[PreSignedUrlResponse]>(
      `${fileUploadBasePath}/GetPresignedUrl`,
      {
        contentType: 'video/mp4',
        uploadType: 'StaticVideo',
        fileExt: 'mp4',
      },
    );

    const {fileKey, presignedUrl, finalS3Url} = urlsObj;
    const cancelTokenSource = axios.CancelToken.source();

    return uploadFileToS3({
      url: presignedUrl,
      file,
      contentType: 'video/mp4',
      cancelToken: cancelTokenSource.token,
      onUploadProgress: (progress: AxiosProgressEvent) => {
        const percentCompleted = Math.round((progress.loaded * 100) / progress.total);
        process.progress(percentCompleted);
      },
    })
      .then(() => {
        return apiService.executePostRequest<[number]>(`${BASE_PATH}/CreateStaticVideoCreative`, {
          fileUrl: finalS3Url,
          fileKey,
          contentType: 'video/mp4',
          name,
          language: DEFAULT_LANGUAGE,
          country,
        });
      })
      .then((res) => {
        process.success();
        process.setLink({
          type: BGProcessLinkType.Href,
          label: `Go to Creative ${res.data[0]}`,
          value: {
            url: Routes.CreativeDynamicParameters,
            pathParams: {creativeId: res.data[0]},
          },
        });

        return res;
      })
      .catch((e) => {
        process.fail(e);
      });
  }

  let dataReq: Promise<JobExecutionParams>;

  switch (+creativeType) {
    case StaticCreativeType.Video:
      dataReq = apiV2Service
        .uploadFile<JobExecutionParams>({
          endpoint: `${V2_BASE_PATH}/CreateStaticVideoCreative`,
          file,
          formDataKey: 'zipFile',
          params: {
            name,
            language: DEFAULT_LANGUAGE,
            country,
          },
        })
        .then((res) => res.data);
      break;
    case StaticCreativeType.Audio:
      dataReq = apiV2Service
        .uploadFile<JobExecutionParams>({
          endpoint: `${V2_BASE_PATH}/CreateStaticAudioCreative`,
          file,
          formDataKey: 'file',
          params: {
            name,
            language: DEFAULT_LANGUAGE,
            country,
          },
        })
        .then((res) => res.data);
      break;
    default:
      const externalLinksJson = await extractExternalLinksFromZip(file);

      dataReq = apiService
        .uploadFile<ImportResponse[]>({
          endpoint: `${BASE_PATH}/CreateStaticDisplayCreative`,
          file,
          formDataKey: 'zipFile',
          params: {
            name,
            language: DEFAULT_LANGUAGE,
            country,
            creativeType,
            isRetinaReady,
          },
          postData: {
            externalLinksJson,
          },
        })
        .then(
          ({
            data: [
              {
                executionId: {executionId},
              },
            ],
          }) => ({executionId}),
        );
  }

  return dataReq
    .then(({executionId: jobId}) => {
      return waitForJob({
        jobId,
        jobName: `Upload Static Creative`,
        subtitle: name,
        bgProcess: process,
      });
    })
    .catch((e) => {
      process.fail(e);
    });
}

async function createImageCreativeFromUpload({
  name,
  country,
  formats,
  isRetinaReady,
}: {
  name: string;
  country: string;
  formats: {width: number; height: number; file: File}[];
  isRetinaReady?: boolean;
}) {
  const payload: PostDataObj = {};

  for (const [idx, {file, width, height}] of formats.entries()) {
    payload[`files[${idx}].file`] = file;
    payload[`files[${idx}].width`] = width;
    payload[`files[${idx}].height`] = height;
  }

  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${V2_BASE_PATH}/CreateStaticImageCreative`,
    {name, language: DEFAULT_LANGUAGE, country, isRetinaReady},
    payload,
    {
      sendAsFormData: true,
    },
  );

  return waitForJob({
    jobId: jobId,
    jobName: `Upload Static Creative`,
    subtitle: name,
  });
}

async function getFileKey(file: File) {
  const {presignedUrl, fileKey} = await fileUploadApi.getPresignedUrlForUpload(
    file.type,
    file.name,
  );

  await uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: file.type,
  });

  return fileKey;
}

async function createAudioCreativeFromUpload(files: StaticCreativeFormItem[]) {
  const bannersMeta: {imageFileKey: string; width: number; height: number}[] = [];
  const audioMeta: {audioFileKey: string; creativeName: string}[] = [];

  await Promise.all(
    files.flatMap((format) =>
      format.formats.map(async ({file, width, height}) => {
        const fileKey = await getFileKey(file);

        if (file.type.startsWith('image/')) {
          bannersMeta.push({
            imageFileKey: fileKey,
            width: width || 0,
            height: height || 0,
          });
        } else {
          audioMeta.push({audioFileKey: fileKey, creativeName: format.name});
        }
      }),
    ),
  );

  audioMeta.forEach(async (audio) => {
    const {data} = await apiV2Service.executePostRequest<JobExecutionParams>(
      `${V2_BASE_PATH}/CreateStaticAudioCreativeWithCompanionBanner`,
      {},
      {
        audioFileKey: audio.audioFileKey,
        companionBannerPayloads: bannersMeta,
        creativeName: audio.creativeName,
      },
    );

    return waitForJob({
      jobId: data.executionId,
      jobName: `Upload Static Audio Creative`,
      subtitle: audio.creativeName,
    });
  });
}

function update(data: {
  id: number;
  name?: string;
  country?: string;
  tag1Value?: string;
  tag2Value?: string;
  tag3Value?: string;
  isDefaultEnabled?: boolean;
}) {
  const tags = ['tag1Value', 'tag2Value', 'tag3Value'] as const;
  tags.forEach((tag) => {
    if (data[tag] === '') data[tag] = 'empty';
  });

  return apiService.executePostRequest(`${BASE_PATH}/Update`, data);
}

function updateDynamicParams(creativeId: number, data: DynamicParameter[]) {
  const payload = toRemoteDynamicParameters(data);

  return apiService.executePostRequest<string[] | null>(
    `${BASE_PATH}/Update`,
    {id: creativeId},
    {Data: JSON.stringify(payload)},
  );
}

async function uploadSubtitles({
  file,
  creativeId,
  options = {},
}: {
  file: File;
  creativeId: number;
  options?: ApiOptions;
}): Promise<string> {
  const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(CLMimeType.SRT);

  await uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: CLMimeType.SRT,
    cancelToken: options?.cancelToken,
  });

  return (
    await apiV2Service.executePostRequest<string>(
      `FileUpload/UploadCreativeFile`,
      {creativeId, fileUrl: finalS3Url, fileName: file.name},
      {},
      options,
    )
  ).data;
}

async function uploadImage({
  creativeId,
  file,
  options = {},
  imageUploadType = ImageUploadType.CreativeImage,
}: {
  creativeId: number;
  file: File;
  options?: ApiOptions;
  imageUploadType?: ImageUploadType;
}): Promise<string> {
  return fileUploadApi.uploadImage({
    objectId: creativeId,
    keyType: 'creativeId',
    imageType: imageUploadType,
    file,
    options,
  });
}

async function uploadVideo({
  creativeId,
  file,
  options,
  paramKey,
  width,
  height,
  extension = 'mp4',
  contentType = 'video/mp4',
}: {
  creativeId: number;
  file: File;
  paramKey: string;
  width: number;
  height: number;
  options?: ApiOptions;
  extension?: string;
  contentType?: string;
}) {
  return fileUploadApi.uploadVideo({
    objectId: creativeId,
    uploadType: VideoUploadType.CreativeVideo,
    file,
    options,
    paramKey,
    width,
    height,
    extension,
    contentType,
  });
}

async function addCreativesToDecisionTree({
  campaignId,
  creatives,
  landingPage,
  landingPageSource,
  utmParams,
}: {
  campaignId: number;
  creatives: Creative[];
  landingPage: string;
  landingPageSource: LandingPageSource;
  utmParams: string;
}) {
  const {nextNodeId, campaignNode, versionNumber} = await apiV2Service
    .executeGetRequest<{
      loadedVersion: {
        number: number;
        decisionTree: {campaignNode: {x: number; y: number}; nextNodeId: number};
      };
    }>(`Campaign/${campaignId}/version/latest`)
    .then((res) => res.data.loadedVersion)
    .then((res) => ({
      ...res.decisionTree,
      versionNumber: res.number,
    }));

  let num = 0;

  const formObj = {
    newNodes: _map(creatives, (creative) => {
      return {
        id: nextNodeId + num++,
        type: NodeType.CREATIVE,
        name: creative.id,
        x: campaignNode.x + STACKED_NODES_SHIFT * num,
        y:
          campaignNode.y -
          NewNodeYShift -
          STACKED_NODES_SHIFT * creatives.length +
          STACKED_NODES_SHIFT * num,
        creativeId: creative.id,
        landingPage,
        landingPageSource,
        utmParams,
        creative: {
          ..._omit(creative, ['baseStyle', 'template', 'productType', 'updateTime']),
          type: creative.type.value,
        },
        nativeAdTitle: '',
        nativeAdDescription: '',
        nativeAdSponsor: '',
        nativeAdCTA: '',
        nativeAdLogo: '',
        nativeAdLogoSize: MINIMUM_NATIVE_LOGO_SIZE,
      };
    }),
  };

  return apiV2Service.executePatchRequest(
    `Campaign/${campaignId}/version/number/${versionNumber}/tree`,
    {},
    formObj,
  );
}

function reGeneratePoster(creativeId: number) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`${V2_BASE_PATH}/${creativeId}/RefreshPoster`)
    .then(({data}) => data)
    .then(({executionId: jobId}) => {
      return waitForJob({
        jobId,
        jobName: `Refresh Poster for Creative ${creativeId}`,
      }).catch(() => {});
    })
    .then(() => get({id: creativeId}))
    .then((data) => data.creative.poster);
}

function reGeneratePosters(
  creativeIds: number[],
  onPosterUpdated: (data: {creativeId: number; poster: string}) => void,
) {
  return Promise.all(
    creativeIds.map((creativeId) =>
      apiV2Service
        .executePostRequest<JobExecutionParams>(`${V2_BASE_PATH}/${creativeId}/RefreshPoster`)
        .then(({data: {executionId: jobId}}) => ({jobId, creativeId}))
        .catch((e) => {
          handleApiError(e);

          return null;
        }),
    ),
  )
    .then((jobs: {jobId: number; jobType: number; creativeId: number}[]) =>
      jobs.filter((data) => !!data?.jobId),
    )
    .then((jobs) => {
      return jobs.map((job) =>
        waitForJob({
          jobId: job.jobId,
          jobName: `Refresh Poster for Creative ${job.creativeId}`,
        })
          .then(() => get({id: job.creativeId}))
          .then(({creative}) =>
            onPosterUpdated({creativeId: job.creativeId, poster: creative.poster}),
          ),
      );
    });
}

function saveAs(id: number, name: string, dynamicParams: DynamicParameter[]) {
  return duplicate([id]).then(([newId]) =>
    Promise.all([
      creativesApi.updateDynamicParams(newId, dynamicParams),
      creativesApi.update({id: newId, name}),
    ]).then(() => newId),
  );
}

async function renameCreatives(creatives: EntityToRename[]) {
  const creativesToRename = creatives.map((creative) => ({
    id: creative.id,
    newName: creative.newName,
  }));

  return apiV2Service.executePatchRequest<void>(
    `${V2_BASE_PATH}/batchRename`,
    {},
    {data: creativesToRename},
  );
}

async function searchCreatives({
  prompt,
  templateTypes,
}: {
  prompt: string;
  templateTypes: TemplateType[];
}) {
  const {data} = await apiV2Service.executeGetRequest<{
    creatives: RemoteCreative[];
    filtersExplanation: string;
  }>(`${V2_BASE_PATH}/SearchCreatives`, {
    prompt,
    templateTypes,
  });

  return data;
}

export const creativesApi = {
  get,
  getAll,
  update,
  getForDecisionTree,
  disconnectFromCampaign,
  deleteCreative,
  duplicate,
  duplicateFromCSV,
  getDuplicateFromCSVFileExample,
  copyDynamicParams,
  connectToCampaign,
  reGeneratePosters,
  addFullSizePromoSlide,
  removeFullSizePromoSlide,
  exportToZip,
  createFromTemplate,
  createFromUpload,
  getAdTagsForCreativePreview,
  updateDynamicParams,
  uploadSubtitles,
  uploadImage,
  uploadVideo,
  addCreativesToDecisionTree,
  duplicateToClient,
  reGeneratePoster,
  saveAs,
  getAllSlim,
  getCreativePosters,
  createImageCreativeFromUpload,
  createAudioCreativeFromUpload,
  renameCreatives,
  searchCreatives,
};
